import axiosClient from '../axiosClient';
import {
  Workspace,
  CreateWorkspaceDto,
  UpdateWorkspaceDto,
  AddWorkspaceMemberDto,
  WorkspaceMemberRole,
  Board,
} from '../../types/common';



export interface GetWorkspacesResponse {
  workspaces: Workspace[];
  total: number;
}

export interface GetWorkspaceResponse {
  workspace: Workspace;
}

export interface CreateWorkspaceResponse {
  workspace: Workspace;
}

export interface UpdateWorkspaceResponse {
  workspace: Workspace;
}

export interface DeleteWorkspaceResponse {
  success: boolean;
  message: string;
}

export interface GetWorkspaceBoardsResponse {
  boards: Board[];
  total: number;
}

export interface AddMemberResponse {
  member: {
    id: string;
    userId: string;
    workspaceId: string;
    role: WorkspaceMemberRole;
    joinedAt: string;
  };
}

export interface RemoveMemberResponse {
  success: boolean;
  message: string;
}

export const workspacesApi = {
  // Get all workspaces for the current user
  getWorkspaces: async (): Promise<GetWorkspacesResponse> => {
    try {
      const response = await axiosClient.get('/workspaces');
      // Backend returns direct array, not wrapped in { workspaces: [...] }
      const workspacesArray = Array.isArray(response.data) ? response.data : response.data.workspaces || [];
      return { workspaces: workspacesArray, total: workspacesArray.length };
    } catch (error: any) {
      console.error('Network error during getWorkspaces', error);
      throw error;
    }
  },

  // Get a specific workspace by ID
  getWorkspace: async (workspaceId: string): Promise<GetWorkspaceResponse> => {
    try {
      const response = await axiosClient.get(`/workspaces/${workspaceId}`);
      // Backend returns workspace directly, not wrapped in { workspace: {...} }
      const workspaceData = response.data.workspace || response.data;
      return { workspace: workspaceData };
    } catch (error: any) {
      console.error(`Network error during getWorkspace ${workspaceId}`, error);
      throw error;
    }
  },

  // Create a new workspace
  createWorkspace: async (data: CreateWorkspaceDto): Promise<CreateWorkspaceResponse> => {
    try {
      const response = await axiosClient.post('/workspaces', data);
      return response.data;
    } catch (error: any) {
      console.error('Network error during createWorkspace', error);
      throw error;
    }
  },

  // Update an existing workspace
  updateWorkspace: async (
    workspaceId: string,
    data: UpdateWorkspaceDto
  ): Promise<UpdateWorkspaceResponse> => {
    try {
      const response = await axiosClient.patch(`/workspaces/${workspaceId}`, data);
      return response.data;
    } catch (error: any) {
      console.error(`Network error during updateWorkspace ${workspaceId}`, error);
      throw error;
    }
  },

  // Delete a workspace
  deleteWorkspace: async (workspaceId: string): Promise<DeleteWorkspaceResponse> => {
    try {
      const response = await axiosClient.delete(`/workspaces/${workspaceId}`);
      return response.data;
    } catch (error: any) {
      console.error(`Network error during deleteWorkspace ${workspaceId}`, error);
      throw error;
    }
  },

  // Get boards for a workspace
  getWorkspaceBoards: async (workspaceId: string): Promise<GetWorkspaceBoardsResponse> => {
    try {
      const response = await axiosClient.get(`/workspaces/${workspaceId}/boards`);
      return response.data;
    } catch (error: any) {
      console.error(`Network error during getWorkspaceBoards ${workspaceId}`, error);
      throw error;
    }
  },

  // Add member to workspace
  // Note: unlike the read endpoints above, member mutations do NOT fall back to mock
  // success on failure — callers (e.g. the invite modal) need real backend errors
  // (duplicate invite, invalid email, insufficient permission) to reach the UI.
  addMember: async (
    workspaceId: string,
    data: AddWorkspaceMemberDto
  ): Promise<AddMemberResponse> => {
    const response = await axiosClient.post(`/workspaces/${workspaceId}/members`, data);
    return response.data;
  },

  // Remove member from workspace
  removeMember: async (workspaceId: string, userId: string): Promise<RemoveMemberResponse> => {
    const response = await axiosClient.delete(`/workspaces/${workspaceId}/members/${userId}`);
    return response.data;
  },

  // Update member role
  updateMemberRole: async (
    workspaceId: string,
    userId: string,
    role: WorkspaceMemberRole
  ): Promise<AddMemberResponse> => {
    const response = await axiosClient.patch(
      `/workspaces/${workspaceId}/members/${userId}`,
      { role }
    );
    return response.data;
  },

  // Search workspaces
  searchWorkspaces: async (query: string): Promise<GetWorkspacesResponse> => {
    try {
      const response = await axiosClient.get('/workspaces/search', {
        params: { q: query },
      });
      return response.data;
    } catch (error: any) {
      console.error('Network error during searchWorkspaces', error);
      throw error;
    }
  },
};
